import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione } from '../../componenti/Pezzi.tsx'
import { Bottone, Scelta } from '../../componenti/base.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import {
  BarraCalendario, Calendario, periodoDellaVista, useStretto, type EventoCalendario, type PuntoTrascinato,
  type PuntoVuoto, type RigaCalendario, type TonoEvento, type TrattoSede, type Vista,
} from '../../componenti/calendario/Calendario.tsx'
import { oggiARoma } from '../../componenti/calendario/date.ts'
import { DettaglioMediazione } from '../mediazioni/CalendarioMediazioni.tsx'
import {
  DettaglioAppuntamento, DettaglioEvento, ModuloEvento, ModuloIndisponibilita, NESSUNA_CONFERMA, NOMI_STRATI, STRATI,
  avvisoSuperabile, conferma, corpoConferme, type CodiceAvviso, type Conferme, type Conflitto, type Impegno,
  type InizialiEvento, type RispostaAgenda, type Strato,
} from './comune.tsx'

/**
 * «Agenda» (559-560; docs/15-calendari-sedi.md, fase 3): l'agenda di una sede del perimetro oppure «La mia
 * agenda», a strati che si accendono e si spengono (appuntamenti, mediazioni, turni, stanze, riunioni,
 * attivita', chiusure), con le viste del componente calendario.
 *
 *  - giorno: una colonna per stanza o per persona in turno;
 *  - settimana: dal lunedi' al sabato, ombreggiata fuori orario, con la fascia grigia di chiusure e feste;
 *  - mese; elenco stampabile; sotto i 600 px l'elenco del giorno.
 *
 * Che cosa si vede lo decide l'API (decisione 3): «Appuntamento» e «Mediazione» con operatore o mediatore e
 * stanza, il nome solo a chi ha la scheda; le riunioni dei soli partecipanti, agli altri, «Occupato» nella
 * stanza; «Non disponibile» a chi programma i turni. Lo spazio vuoto apre «Nuovo impegno» con giorno, ora e
 * stanza o persona della colonna; turni e prenotazioni si trascinano (un impegno ripetuto solo nello stesso
 * giorno: diventa un'eccezione di quel giorno).
 */

type Colonne = 'stanze' | 'persone'
const VISTE: Vista[] = ['giorno', 'settimana', 'mese', 'elenco']
/** Riga degli impegni che non hanno stanza o persona, nel giorno a colonne. */
const ALTRO = '-'

const tonoDi = (conflitti: Conflitto[], stato: string | null): TonoEvento => {
  if (stato === 'annullato' || stato === 'annullata') return 'annullato'
  if (conflitti.some((c) => c.gravita === 'rosso')) return 'rosso'
  if (conflitti.some((c) => c.gravita === 'arancione')) return 'arancione'
  return stato && ['fatto', 'svolta', 'non_presentata'].includes(stato) ? 'concluso' : 'normale'
}

type Aperto = { tipo: 'mediazione' | 'appuntamento'; id: string } | { tipo: 'evento'; id: string; giorno: string }

export function Agenda() {
  const { io } = useSessione()
  const stretto = useStretto()
  const [parametri, setParametri] = useSearchParams()
  const vista = (VISTE as string[]).includes(parametri.get('vista') ?? '') ? parametri.get('vista') as Vista : 'settimana'
  const giorno = /^\d{4}-\d{2}-\d{2}$/.test(parametri.get('giorno') ?? '') ? parametri.get('giorno')! : oggiARoma()
  const mia = parametri.get('sede') === 'mia'
  const sedeChiesta = mia ? '' : parametri.get('sede') ?? ''
  const colonne: Colonne = parametri.get('colonne') === 'persone' ? 'persone' : 'stanze'
  const spenti = new Set((parametri.get('spenti') ?? '').split(',').filter(Boolean))
  const strati = STRATI.filter((s) => !spenti.has(s))

  const [dati, setDati] = useState<RispostaAgenda | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [altraSede, setAltraSede] = useState(false)
  const [aperto, setAperto] = useState<Aperto | null>(null)
  const [nuovo, setNuovo] = useState<InizialiEvento | null>(null)
  const [assenza, setAssenza] = useState(false)
  const [spostamento, setSpostamento] = useState<{ codice: CodiceAvviso; messaggio: string; riprova: () => void } | null>(null)
  const [versione, setVersione] = useState(0)
  const ricarica = () => setVersione((v) => v + 1)

  const cambia = useCallback((valori: Record<string, string>) => {
    const nuovi = new URLSearchParams(parametri)
    for (const [k, v] of Object.entries(valori)) { if (v) nuovi.set(k, v); else nuovi.delete(k) }
    setParametri(nuovi, { replace: true })
  }, [parametri, setParametri])

  const { dal, al } = periodoDellaVista(vista, giorno)
  const richiesta = useMemo(() => {
    const q = new URLSearchParams({ dal, al, strati: strati.join(',') })
    if (mia) q.set('mia', '1')
    else if (sedeChiesta) q.set('sede', sedeChiesta)
    return `/agenda?${q}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dal, al, mia, sedeChiesta, parametri])
  useEffect(() => {
    let vivo = true
    setErrore(null)
    api.get<RispostaAgenda>(richiesta)
      .then((d) => { if (vivo) setDati(d) })
      .catch((e) => {
        if (!vivo) return
        // Una sede che non e' nell'agenda di chi guarda (per esempio dall'avviso di una chiusura per una
        // mediazione fissata dalla scheda): si apre l'agenda di partenza, e lo si dice.
        if (e instanceof ErroreApi && e.codice === 'FUORI_PERIMETRO' && sedeChiesta) {
          setAltraSede(true)
          setParametri((p) => { const n = new URLSearchParams(p); n.delete('sede'); return n }, { replace: true })
          return
        }
        setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare l’agenda.')
      })
    return () => { vivo = false }
  }, [richiesta, versione, sedeChiesta, setParametri])

  const sedeId = dati?.sede?.id ?? null
  // Sul telefono il giorno e' un elenco: niente colonne.
  const conColonne = vista === 'giorno' && !mia && !stretto
  const righe: RigaCalendario[] | undefined = useMemo(() => {
    if (!dati || !conColonne) return undefined
    if (colonne === 'persone') {
      const inTurno = dati.persone.filter((p) => dati.impegni.some((i) => i.persona_colonna === p.chiave && i.giorno === giorno))
      return [...inTurno.map((p) => ({ chiave: p.chiave, titolo: `${p.cognome} ${p.nome}`, sottotitolo: p.tipo === 'mediatore' ? 'mediatore' : undefined })),
        { chiave: ALTRO, titolo: 'Altri impegni' }]
    }
    return [...dati.stanze.map((s) => ({ chiave: s.id, titolo: s.nome, sottotitolo: s.capienza ? `${s.capienza} posti` : undefined })),
      { chiave: ALTRO, titolo: 'Senza stanza' }]
  }, [dati, conColonne, colonne, giorno])

  const eventi: EventoCalendario[] = useMemo(() => {
    if (!dati) return []
    // Un impegno la cui stanza o persona non ha una colonna (stanza non piu' in uso, mediatore di un'altra
    // sede) va fra gli altri: nessuno resta fuori dal giorno.
    const colonneDelGiorno = new Set((righe ?? []).map((r) => r.chiave))
    const colonnaDi = (i: Impegno) => {
      const k = colonne === 'persone' ? i.persona_colonna : i.risorsa_id
      return k && colonneDelGiorno.has(k) ? k : ALTRO
    }
    return dati.impegni.map((i): EventoCalendario => ({
      chiave: i.chiave, giorno: i.giorno, dalle: i.dalle, alle: i.alle, titolo: i.titolo,
      dettaglio: i.dettaglio ?? undefined,
      riga: conColonne ? colonnaDi(i) : undefined,
      strato: i.strato, tono: tonoDi(i.conflitti, i.stato), avvisi: i.conflitti.map((c) => c.messaggio),
      tuttoIlGiorno: i.tutto_il_giorno, trascinabile: i.trascinabile, occupato: i.occupato,
      // Nella settimana i turni e le indisponibilita' stanno in testa al giorno: nella griglia lunga mattinata
      // schiaccerebbero appuntamenti e mediazioni. Si vedono a colonne nel giorno.
      inTesta: (vista === 'settimana' && (i.tipo === 'turno' || i.tipo === 'indisponibile'))
        || (conColonne && colonne === 'stanze' && i.tipo === 'indisponibile'),
      apri: i.occupato || i.tipo === 'attivita_collettiva' || (i.tipo === 'indisponibile' && !i.modificabile) ? undefined
        : i.tipo === 'mediazione' ? () => setAperto({ tipo: 'mediazione', id: i.id })
          : i.tipo === 'appuntamento' ? () => setAperto({ tipo: 'appuntamento', id: i.id })
            : () => setAperto({ tipo: 'evento', id: i.id, giorno: i.giorno }),
    }))
  }, [dati, conColonne, colonne, vista, righe])

  const puoCreare = !!dati && (dati.puo.scrivere || dati.puo.turni)
  const crea = (p: Partial<PuntoVuoto>) => {
    setEsito(null)
    const oggi = oggiARoma()
    const riga = p.riga && p.riga !== ALTRO ? p.riga : undefined
    setNuovo({
      giorno: p.giorno && p.giorno >= oggi ? p.giorno : oggi, dalle: p.ora,
      sede_id: sedeId, risorsa_id: colonne === 'stanze' ? riga : undefined,
      persona: colonne === 'persone' ? riga : undefined,
    })
  }

  /**
   * Un turno o una prenotazione lasciati altrove. Impegno singolo: giorno, ore e (dalla colonna) stanza o
   * persona nuovi. Ripetuto: solo nello stesso giorno, come eccezione di quel giorno. Sede chiusa e persona
   * non disponibile sono avvisi: «Sposta lo stesso» riprova con le conferme date.
   */
  async function trascina(e: EventoCalendario, a: PuntoTrascinato, conferme: Conferme = NESSUNA_CONFERMA) {
    const impegno = dati?.impegni.find((i) => i.chiave === e.chiave)
    if (!impegno) return
    setErrore(null); setSpostamento(null)
    const durata = minutiDi(impegno.alle) - minutiDi(impegno.dalle)
    const alle = oraDi(Math.min(minutiDi(a.ora) + durata, 1440))
    const riga = a.riga && a.riga !== ALTRO ? a.riga : undefined
    const colonna: Record<string, unknown> = {}
    if (riga && conColonne && colonne === 'stanze' && riga !== impegno.risorsa_id) colonna.risorsa_id = riga
    if (riga && conColonne && colonne === 'persone' && impegno.tipo === 'turno' && riga !== impegno.persona_turno) {
      if (impegno.ricorrente) colonna[riga.startsWith('u:') ? 'sostituto_utente_id' : 'sostituto_mediatore_id'] = riga.slice(2)
      else colonna[riga.startsWith('u:') ? 'utente_id' : 'mediatore_id'] = riga.slice(2)
    }
    try {
      if (impegno.ricorrente) {
        if (a.giorno !== impegno.giorno) {
          setErrore('Un impegno che si ripete si sposta solo nello stesso giorno. Per cambiare i giorni aprilo e modifica la ripetizione.')
          return
        }
        await api.put(`/agenda/eventi/${impegno.id}/occorrenze/${impegno.giorno}`,
          { dalle: a.ora, alle, ...colonna, ...corpoConferme(conferme) })
      } else {
        await api.patch(`/agenda/eventi/${impegno.id}`, { giorno: a.giorno, dalle: a.ora, alle, ...colonna, ...corpoConferme(conferme) })
      }
      setEsito(`Spostato ${a.giorno === impegno.giorno ? '' : 'a un altro giorno, '}dalle ${a.ora} alle ${alle}.`)
      ricarica()
    } catch (err) {
      const codice = err instanceof ErroreApi ? avvisoSuperabile(err.codice) : null
      if (codice) {
        setSpostamento({ codice, messaggio: (err as ErroreApi).message, riprova: () => void trascina(e, a, conferma(conferme, codice)) })
      } else setErrore(messaggioErrore(err))
    }
  }

  const opzioniSede = [...(dati?.sedi ?? []).map((s) => ({ valore: s.id, etichetta: s.nome })), { valore: 'mia', etichetta: 'La mia agenda' }]
  const sedeScelta = mia ? 'mia' : (sedeChiesta || sedeId || '')
  const filtri = (
    <div className="calendario-filtri">
      <Scelta etichetta="Agenda" valore={sedeScelta} cambia={(v) => cambia({ sede: v })} opzioni={opzioniSede} />
      {conColonne && (
        <Scelta etichetta="Una colonna per" valore={colonne} cambia={(v) => cambia({ colonne: v === 'persone' ? 'persone' : '' })}
                opzioni={[{ valore: 'stanze', etichetta: 'Stanza' }, { valore: 'persone', etichetta: 'Persona in turno' }]} />
      )}
      <fieldset className="agenda-strati">
        <legend>Si vedono</legend>
        {STRATI.map((s) => (
          <label key={s} className="scelta">
            <input type="checkbox" checked={!spenti.has(s)}
                   onChange={(ev) => {
                     const nuovi = new Set(spenti)
                     if (ev.target.checked) nuovi.delete(s); else nuovi.add(s)
                     cambia({ spenti: [...nuovi].join(',') })
                   }} />
            {NOMI_STRATI[s as Strato]}
          </label>
        ))}
      </fieldset>
    </div>
  )

  return (
    <>
      <Intestazione titolo={mia ? 'La mia agenda' : `Agenda${dati?.sede ? ` — ${dati.sede.nome}` : ''}`}
                    sottotitolo={mia
                      ? 'I tuoi turni, appuntamenti, mediazioni e riunioni, in tutte le sedi.'
                      : `Appuntamenti, mediazioni, turni, stanze e riunioni della sede.${puoCreare ? ' Fai clic su uno spazio vuoto per fissare un impegno.' : ''}`}
                    azioni={<Bottone variante="secondario" dimensione="piccolo" onClick={() => setAssenza(true)}>Segnati non disponibile</Bottone>} />
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {altraSede && <Avviso>Quella sede non è fra quelle della tua agenda: vedi la tua sede di partenza.</Avviso>}
      {stretto ? <details className="calendario-filtri-telefono"><summary>Filtri</summary>{filtri}</details> : filtri}

      <BarraCalendario vista={vista} cambiaVista={(v) => cambia({ vista: v })} giorno={giorno}
                       cambiaGiorno={(g) => cambia({ giorno: g })} viste={VISTE}
                       nuovo={puoCreare ? () => crea({ giorno }) : undefined} etichettaNuovo="Nuovo impegno" />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {spostamento && (
        <Avviso tipo="attenzione">
          {spostamento.messaggio}
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" onClick={spostamento.riprova}>Sposta lo stesso</Bottone>
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => setSpostamento(null)}>Lascia com’era</Bottone>
          </div>
        </Avviso>
      )}
      {!dati && !errore && <Attesa testo="Caricamento dell’agenda…" />}
      {dati && (
        <>
          <Calendario vista={vista} giorno={giorno} eventi={eventi} righe={righe}
                      statoSede={dati.stato_sede as TrattoSede[] | null}
                      etichettaRighe={colonne === 'persone' ? 'Persona' : 'Stanza'} etichettaVuoto="Nuovo impegno"
                      vuoto="Niente in agenda." alVuoto={puoCreare ? crea : undefined}
                      apriGiorno={(g) => cambia({ vista: 'giorno', giorno: g })}
                      trascina={(e, a) => void trascina(e, a)} />
          {conColonne && colonne === 'stanze' && !dati.stanze.length && (
            <p className="tenue piccolo">
              La sede non ha stanze. Le aggiunge chi coordina la sede, dalla pagina della sede, linguetta «Stanze».
            </p>
          )}
          <ul className="calendario-legenda">
            <li><span className="calendario-campione calendario-tono-rosso" /> Stessa persona o stessa stanza due volte</li>
            <li><span className="calendario-campione calendario-tono-arancione" /> Sede chiusa, fuori orario o persona non disponibile</li>
            <li><span className="calendario-campione calendario-occupato" /> Occupato: un impegno che non vedi</li>
            {(dati.puo.turni || dati.puo.scrivere) && !stretto && <li>Turni e prenotazioni si spostano trascinandoli.</li>}
          </ul>
          {!mia && sedeId && (
            <p className="tenue piccolo">
              Orari, chiusure e stanze della sede: <Link to={`/sedi/${sedeId}`}>pagina della sede</Link>.
            </p>
          )}
        </>
      )}

      {aperto?.tipo === 'mediazione' && <DettaglioMediazione id={aperto.id} chiudi={() => setAperto(null)} />}
      {aperto?.tipo === 'appuntamento' && <DettaglioAppuntamento id={aperto.id} chiudi={() => setAperto(null)} />}
      {aperto?.tipo === 'evento' && dati && (
        <DettaglioEvento id={aperto.id} giorno={aperto.giorno} dati={dati} chiudi={() => setAperto(null)}
                         fatto={(testo) => { setAperto(null); setEsito(testo); ricarica() }} />
      )}
      {nuovo && dati && (
        <ModuloEvento iniziali={nuovo} dati={dati} mia={mia} utenteId={io?.id ?? ''} chiudi={() => setNuovo(null)}
                      fatto={(testo) => { setNuovo(null); setEsito(testo); ricarica() }} />
      )}
      {assenza && (
        <ModuloIndisponibilita chiudi={() => setAssenza(false)}
                               fatto={(testo) => { setAssenza(false); setEsito(testo); ricarica() }} />
      )}
    </>
  )
}

const minutiDi = (o: string) => Number(o.slice(0, 2)) * 60 + Number(o.slice(3, 5))
const oraDi = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
